
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import LIST_DOCUMENTS from "@/apollo/documents/ListDocuments.gql";

import CardPagination from "@/components/card/CardPagination.vue";
import EmptyListPlaceholderSidebar from "@/components/common/EmptyListPlaceholderSidebar.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import Search from "@/components/common/Search.vue";
import DocumentItem from "@/components/documents/DocumentItem.vue";
import ActionsPanelWithFilters from "@/components/common/ActionsPanelWithFilters.vue";

import SearchableListConfig from "@/entities/SearchableListConfig";
import { Document, User } from "@/generated/graphql";
import searchableListConfig from "@/data/searchableListConfig";
import EmptyListImageDocuments from "@/components/icons/emptyListImages/EmptyListImageDocuments.vue";

import DocumentTable from "@/components/documents/DocumentTableNew.vue";

interface SearchQuery {
  filename: { _ilike: string };
}

@Component({
  components: {
    EmptyListImageDocuments,
    CardPagination,
    EmptyListPlaceholderSidebar,
    LoadingOverlay,
    Search,
    DocumentItem,
    ActionsPanelWithFilters,
    DocumentTable,
  },
  apollo: {
    documents: {
      query: LIST_DOCUMENTS,
      skip() {
        return !this.showFileList;
      },
      variables() {
        return this.contractorDocumentArgs;
      },
      update(data) {
        this.searchableListConfig.total = data.total.aggregate.count;
        this.searchableListConfig.loaded = data.document.length;
        return data.document;
      },
    },
  },
})
export default class SearchableDocumentsList extends Vue {
  @Prop({ type: String, default: undefined }) readonly userFullName?: string;
  @Prop({ type: String, default: undefined })
  readonly attachedToUserId?: String;
  @Prop({ type: String, default: undefined }) readonly projectId?: string;
  @Prop({ type: Boolean, default: false }) readonly isProject!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isCustomHeight!: boolean;
  @Prop({ type: Object, default: () => ({}) }) readonly query!: Object;
  @Prop({ type: Boolean, default: false }) readonly frozen!: boolean;
  @Prop({ type: String, default: "" }) readonly searchByDocumentName!: string;
  @Prop({ type: String, default: "" }) readonly classCarePlan!: string;
  @Prop({ type: Boolean, default: false })
  readonly outerFilterTouched!: boolean;
  @Prop({ type: Boolean, default: false }) readonly extraInfoHidden!: boolean;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, default: undefined }) readonly customRequestId?: string;
  @Prop({ type: String, default: undefined }) readonly companyId?: string;
  @Prop({ type: String, required: true }) readonly clientId?: string;
  @Prop({ type: Boolean, default: true }) readonly allowUploads!: boolean;
  @Prop({ type: Boolean, default: true }) readonly showFileList!: boolean;
  @Prop({ type: Boolean, default: false }) readonly showMissingClientMessage!: boolean;

  documents: Document[] | undefined = undefined;
  searchTouched: boolean = false;
  searchableListConfig: SearchableListConfig = { ...searchableListConfig };

  get search(): string {
    return this.searchableListConfig.search || this.searchByDocumentName;
  }

  get isEmptyListActionsPassed(): boolean {
    return Boolean(this.$scopedSlots.emptyListActions);
  }
  get emptyListTitle(): string {
    if (this.showMissingClientMessage) {
      return "Please create the client before uploading documents.";
    }
    if (!this.isEmptyListActionsPassed) {
      return "No available documents";
    }
    if (this.userFullName) {
      return ``;
    }
    return "Start adding your documents";
  }

  get emptyListSubtitle(): string {
    if (!this.clientId) {
      return "";
    }
    if (this.frozen) {
      return "No available documents";
    }
    if (this.userFullName) {
      return `No available documents`;
    }
    return "";
  }

  get contractorDocumentArgs(): any {
    const { page, limit } = this.searchableListConfig;
    const nextOffset = page * limit - limit;
    const params = {
      offset: nextOffset,
      limit: limit,
    };
    const searchQuery: SearchQuery = {
      filename: { _ilike: `%${this.search}%` },
    };

    if(this.clientId) {
      searchQuery["document_projects"] = { project_id: { _eq: this.clientId } };
    }
    return {
      where: {
        ...searchQuery,
        ...this.query
      },
    };
  }


  get isDocumentsPresent(): boolean {
    if (this.documents) {
      return this.documents.length > 0;
    }
    return false;
  }

  get isDocumentsExist(): boolean {
    return (
      this.searchTouched || this.outerFilterTouched || this.isDocumentsPresent
    );
  }

  get isSearchResultEmpty(): boolean {
    return (
      !this.isDocumentsPresent &&
      (this.searchTouched || this.outerFilterTouched)
    );
  }

  get isEmptyListPlaceholderShown(): boolean {
    return !this.isDocumentsExist && !this.$apollo.loading;
  }

  @Watch("searchByDocumentName")
  onOutsideSearchChange() {
    if (!this.searchTouched) {
      this.searchTouched = true;
    }
  }

  isFrozen(document: Document): boolean {
    if (this.frozen) {
      return true;
    }

    if (this.currentUser.role === "ADMIN") {
      return false;
    }

    if (this.customRequestId) {
      return false;
    }

    if (!this.currentUser) {
      return false;
    }

    return this.currentUser.id !== document.uploaded_by;
  }

  onSearch(value: string): void {
    if (!this.searchTouched) {
      this.searchTouched = true;
    }
    this.searchableListConfig.search = value;
  }

  async updateDocumentList(): Promise<void> {
    this.$emit("listChanged");
    await this.$apollo.queries.documents.refetch();
    this.$emit("documentNumberChanged", this.searchableListConfig.total);
  }
}
